// Menu taxonomy shared by the browse filter and the menu edit form.
// Mirrors webapp/backend/src/constants/menuTaxonomy.js — keep the two in sync.
//
// Closed sets on purpose: the edit form used to bucket every catalog tag by
// substring ("salad" pulled in "Asian salad dressing"), which is why it never
// matched the filter panel.

export const MEAL_TYPE_CATEGORIES = [
  'breakfast',
  'brunch',
  'lunch',
  'dinner',
  'appetizer',
  'snack',
  'dessert',
  'side',
  'drink'
];

export const MEAL_TYPE_LABELS = {
  breakfast: 'Breakfast',
  brunch: 'Brunch',
  lunch: 'Lunch',
  dinner: 'Dinner',
  appetizer: 'Appetizer',
  snack: 'Snack',
  dessert: 'Dessert',
  side: 'Side',
  drink: 'Drink'
};

export const COOKING_METHODS = [
  'baked',
  'fried',
  'grilled',
  'roasted',
  'sauteed',
  'steamed',
  'boiled',
  'slow-cooked',
  'pressure-cooked',
  'air-fried',
  'smoked',
  'braised'
];

export const COOKING_METHOD_LABELS = {
  baked: 'Baked',
  fried: 'Fried',
  grilled: 'Grilled',
  roasted: 'Roasted',
  sauteed: 'Sauteed',
  steamed: 'Steamed',
  boiled: 'Boiled',
  'slow-cooked': 'Slow-cooked',
  'pressure-cooked': 'Pressure-cooked',
  'air-fried': 'Air-fried',
  smoked: 'Smoked',
  braised: 'Braised'
};

const COOKING_METHOD_ALIASES = {
  'air fried': 'air-fried',
  'airfryer': 'air-fried',
  'air-fryer': 'air-fried',
  'air fryer': 'air-fried',
  'instant pot': 'pressure-cooked',
  'instant-pot': 'pressure-cooked',
  'pressure cooked': 'pressure-cooked',
  'slow cooked': 'slow-cooked',
  'crockpot': 'slow-cooked',
  'crock pot': 'slow-cooked',
  'slow cooker': 'slow-cooked',
  'saute': 'sauteed',
  'sauté': 'sauteed',
  'sautéed': 'sauteed',
  'stir-fried': 'fried',
  'stir fried': 'fried',
  'deep-fried': 'fried',
  'deep fried': 'fried',
  'pan-fried': 'fried',
  'pan fried': 'fried'
};

export const PROTEINS = [
  'chicken',
  'turkey',
  'beef',
  'pork',
  'lamb',
  'fish',
  'shrimp',
  'seafood',
  'tofu',
  'beans',
  'eggs'
];

export const PROTEIN_LABELS = {
  chicken: 'Chicken',
  turkey: 'Turkey',
  beef: 'Beef',
  pork: 'Pork',
  lamb: 'Lamb',
  fish: 'Fish',
  shrimp: 'Shrimp',
  seafood: 'Seafood',
  tofu: 'Tofu',
  beans: 'Beans',
  eggs: 'Eggs'
};

const PROTEIN_PATTERNS = [
  { id: 'chicken', regex: /\bchicken\b|\bpoulet\b/i },
  { id: 'turkey', regex: /\bturkey\b/i },
  { id: 'beef', regex: /\bbeef\b|\bsteak\b|\bbrisket\b|\bshort ribs?\b|\bchuck roast\b|\bground chuck\b/i },
  { id: 'pork', regex: /\bpork\b|\bbacon\b|\bham\b|\bpancetta\b|\bprosciutto\b|\bchorizo\b|\bpulled pork\b/i },
  { id: 'lamb', regex: /\blamb\b/i },
  { id: 'fish', regex: /\bsalmon\b|\btuna\b|\bcod\b|\btilapia\b|\bflounder\b|\bswordfish\b|\bhalibut\b|\btrout\b|\bbass\b|\bmahi\b|\bsnapper\b|\broughy\b|\bfish\b/i },
  { id: 'shrimp', regex: /\bshrimp\b|\bprawn/i },
  { id: 'seafood', regex: /\bcrab\b|\bscallop\b|\blobster\b|\bclam\b|\bmussel\b|\boyster\b|\bsquid\b|\boctopus\b|\bcalamari\b|\bseafood\b/i },
  { id: 'tofu', regex: /\btofu\b|\btempeh\b|\bseitan\b/i },
  { id: 'beans', regex: /\bchickpeas?\b|\blentils?\b|\bblack beans?\b|\bkidney beans?\b|\bpinto beans?\b|\bcannellini\b|\bwhite beans?\b|\bgarbanzo\b|\bedamame\b/i }
];

const EGG_TITLE_REGEX = /\beggs?\b|\bomelette\b|\bomelet\b|\bfrittata\b|\bquiche\b|\bshakshuka\b|\begg bites?\b/i;
const SAUSAGE_REGEX = /\bsausage\b/i;

export const MAX_OTHER_TAGS = 3;

// Operational leftovers from the import pipeline. Never shown on recipe pages.
export const SYSTEM_TAGS = [
  'estimated-nutrition',
  'estimated-nutrition-ai',
  'incomplete',
  'ai-generated-image',
  'image-generation-failed',
  'qc-flagged'
];

export function isSystemTag(value) {
  return SYSTEM_TAGS.includes(simplify(value).replace(/\s+/g, '-'));
}

/** Tags a cook should see. Strips pipeline/QC metadata. */
export function publicTagsFrom(tags = []) {
  return (Array.isArray(tags) ? tags : []).filter((tag) => !isSystemTag(tag));
}

function simplify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeCookingMethod(value) {
  const key = simplify(value);
  if (!key) return null;
  if (COOKING_METHODS.includes(key)) return key;
  return COOKING_METHOD_ALIASES[key] || null;
}

export function isCookingMethodTag(value) {
  return Boolean(normalizeCookingMethod(value));
}

export function isMealTypeTag(value) {
  return MEAL_TYPE_CATEGORIES.includes(simplify(value));
}

export function isProteinTag(value) {
  return PROTEINS.includes(simplify(value));
}

function ingredientText(ingredient) {
  if (!ingredient) return '';
  if (typeof ingredient === 'string') return ingredient;
  return String(ingredient.name || '');
}

function proteinHaystack(text) {
  return String(text || '').replace(
    /\b(chicken|turkey|beef|veal|pork|ham|fish|oyster|shrimp|prawn)\s+(broth|stock|sauce|bouillon|base|cube)s?\b/gi,
    ''
  );
}

export function normalizeProteins(values = []) {
  const out = new Set();
  (Array.isArray(values) ? values : [values]).forEach((value) => {
    const key = simplify(value);
    if (PROTEINS.includes(key)) out.add(key);
  });
  return PROTEINS.filter((id) => out.has(id));
}

/** Infer proteins from stored field, then fall back to title + ingredients. */
export function itemProteins(item = {}) {
  const stored = normalizeProteins(item.proteins);
  if (stored.length) return stored;

  const found = new Set();
  const haystacks = [
    proteinHaystack(item.title),
    ...(Array.isArray(item.ingredients) ? item.ingredients.map((row) => proteinHaystack(ingredientText(row))) : [])
  ];

  haystacks.forEach((text) => {
    PROTEIN_PATTERNS.forEach(({ id, regex }) => {
      if (regex.test(text)) found.add(id);
    });
    if (SAUSAGE_REGEX.test(text) && !/\bchicken\b|\bturkey\b/i.test(text)) {
      found.add('pork');
    }
  });

  if (EGG_TITLE_REGEX.test(String(item.title || ''))) found.add('eggs');
  return PROTEINS.filter((id) => found.has(id));
}

export function itemCookingMethods(item = {}) {
  const found = new Set();
  (item.tags || []).forEach((tag) => {
    const method = normalizeCookingMethod(tag);
    if (method) found.add(method);
  });
  return COOKING_METHODS.filter((id) => found.has(id));
}

export function isReservedFilterTag(value, dietary = []) {
  const key = simplify(value);
  if (!key) return true;
  if (isMealTypeTag(key) || isCookingMethodTag(key) || isProteinTag(key) || isSystemTag(key)) return true;
  return dietary.some((entry) => simplify(entry) === key);
}

export function otherTagsFrom(tags = [], dietary = []) {
  return (tags || []).filter((tag) => !isReservedFilterTag(tag, dietary));
}
